import { madeWithGlide } from "common/named-icons-lite";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import { useEffect, useState } from "react";
import { TailwindThemeProvider } from "sharedUI/lib/tailwind-theme-provider";
import { getSpinner } from "webapp/chrome/views/activity-spinner/activity-spinner";
import { PlayerContentPlaceholder } from "webapp/glide-app-player.style";
import { browserMightBeOniOS } from "../../../../../lib/browser-detect";
import type { BaseProps } from "../custom-sign-in-props";
import { canShowExternalAuthButtons } from "../google-sso";
import { CustomWireSignInStyle } from "./custom-wire-sign-in-style";
import WireEmailInput from "./wire-email-input";
import WireNameInput from "./wire-name-input";
import WirePasswordInput from "./wire-password-input";

const MadeWithGlideIcon = madeWithGlide;

interface Props extends BaseProps {}

const CustomSignInImpl: React.FunctionComponent<Props> = p => {
    const [visualViewportHeight, setVisualViewportHeight] = useState<number | undefined>(undefined);
    const theme = useWireAppTheme();

    useEffect(() => {
        // onMount
        if (window.visualViewport !== undefined && browserMightBeOniOS) {
            (window.visualViewport as any).addEventListener("scroll", viewportHandler);
            (window.visualViewport as any).addEventListener("resize", viewportHandler);
        }
        return () => {
            //unmount
            if (window.visualViewport !== undefined && browserMightBeOniOS) {
                (window.visualViewport as any).removeEventListener("scroll", viewportHandler);
                (window.visualViewport as any).removeEventListener("resize", viewportHandler);
            }
        };
    }, []);

    const viewportHandler = (e: any) => {
        setVisualViewportHeight(e.target.height);
    };

    const { loginType, colorTheme, currentState, background, isPro, useParallax, appFeatures, previewMode } = p;
    let buttonOffset = 0;

    if (visualViewportHeight !== undefined && window.innerHeight !== visualViewportHeight && window.innerHeight > 500) {
        buttonOffset = window.innerHeight - visualViewportHeight;
        // FIXME: This is a hack to make the virtual keyboard overlap the Sign in with Google
        // button. This only works when there's one external auth button, too.
        // Either make this method report the number of auth buttons we can show or
        // figure out a better way to handle keyboard overlap.
        if (canShowExternalAuthButtons(appFeatures)) {
            buttonOffset -= 66;
        }
    }
    if (currentState === "sign-in") {
        const showBackgroundImage =
            appFeatures?.useCustomSignInBackgroundImageInLoading === true && background !== undefined;
        return (
            <TailwindThemeProvider theme={theme}>
                <PlayerContentPlaceholder
                    data-test="app-signing-in"
                    backgroundImage={showBackgroundImage ? background : undefined}>
                    {!isPro && <MadeWithGlideIcon fgColor={theme.fgColorDark} />}
                    <div className="spinner">{getSpinner(theme.primaryAccentColor, "loading-placeholder")}</div>
                </PlayerContentPlaceholder>
            </TailwindThemeProvider>
        );
    }

    return (
        <CustomWireSignInStyle
            colorTheme={colorTheme}
            useParallax={useParallax === true && currentState === "challenge"}
            isBuilder={true}
            buttonOffset={buttonOffset}
            previewMode={previewMode}
            role="dialog"
            aria-modal={true}
            aria-labelledby="sign-in-description">
            <div className="bc-main-content">
                <div className="bc-content-container">
                    {loginType === "none" && <WireNameInput {...p} />}
                    {loginType === "password" && <WirePasswordInput {...p} />}
                    {loginType === "email" && <WireEmailInput theme={theme} {...p} />}
                </div>
            </div>
        </CustomWireSignInStyle>
    );
};
const CustomWireSignin = CustomSignInImpl;
export default CustomWireSignin;
